import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { EventBusService } from 'shared/services/event-bus.service';
import { ToastService } from 'shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { FormDifferService } from 'shared/services/form-differ.service';
import { User } from 'users/interfaces/user.interface';
import { ShijiUsersService } from 'users/services/shiji-users.service';
import { ShijiUserPermission, ShijiUserPermissionRequest } from 'shared/interfaces/shiji-user-permission.interface';
import { keyvalueOriginalOrder } from 'shared/util/key-value-original-order';
import { TabComponentInterface } from 'shared/interfaces/tab-component.interface';
import keys from 'lodash-es/keys';

@Component({
    selector: 'app-shiji-user-edit-permissions',
    templateUrl: './shiji-user-edit-permissions.component.html',
    styleUrls: ['./shiji-user-edit-permissions.component.scss'],
    standalone: false,
})
export class ShijiUserEditPermissionsComponent implements OnInit, OnDestroy, TabComponentInterface {
    public sorter = keyvalueOriginalOrder;
    public data!: User;

    private subscriptions: { [key: string]: Subscription | undefined } = {};

    public editUserPermissionForm: UntypedFormGroup = this.formBuilder.group({});
    public shijiAccessZoneLabels: any = {};

    constructor(
        private formBuilder: UntypedFormBuilder,
        private shijiUsersService: ShijiUsersService,
        private eventBusService: EventBusService,
        private toastService: ToastService,
        private translate: TranslateService,
        private formDifferService: FormDifferService,
    ) {}

    public ngOnInit(): void {
        this.subscriptions.modalMainAction = this.eventBusService.getObservableForEventStream('modalMainButtonAction').subscribe((data) => {
            if (data === 'permissions') {
                this.updateShijiUserPermissions();
            }
        });

        this.editUserPermissionForm.reset();
        this.shijiAccessZoneLabels = {};
        this.subscriptions.getUserPermissions = this.shijiUsersService
            .getShijiUserPermissions(this.data.id!)
            .subscribe((data: ShijiUserPermission[]) => {
                data.forEach((x) => {
                    this.editUserPermissionForm.addControl(x.id, new UntypedFormControl(x.isAuthorized));
                    this.shijiAccessZoneLabels[x.id] = this.translate.instant(
                        `users.shijiUsers.edit.permissions.shijiAccessZone.${x.shijiAccessZone}`,
                    );
                });

                this.formDifferService.registerForm(this.editUserPermissionForm);
                this.formDifferService.createDataSnapshot();
            });
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public canDeactivateTab(): boolean {
        const canDeactivate = !this.formDifferService.hasDataChanged();

        if (!canDeactivate) {
            this.toastService.showToast('generalKeywords.unsavedTabChanges', 'error', {}, 8000);
        }

        return canDeactivate;
    }

    public updateShijiUserPermissions() {
        this.editUserPermissionForm.markAllAsTouched();
        if (this.editUserPermissionForm.invalid) {
            return;
        }

        const diff = this.formDifferService.getDiffWithSnapshot();
        const toUpdateKeys = keys(diff);
        if (!toUpdateKeys?.length) {
            this.closeModal(false);
        }

        const observablesStream: any = [];
        toUpdateKeys.forEach((permissionId) => {
            const body = {
                id: permissionId,
                isAuthorized: diff[permissionId],
            } as ShijiUserPermissionRequest;
            observablesStream.push(this.shijiUsersService.updateShijiUserPermission(body));
        });

        this.subscriptions.forkJoin = forkJoin(observablesStream).subscribe((response: any) => {
            this.toastService.showToast('users.shijiUsers.edit.basic.updateSuccess', 'success', {
                firstName: this.data.firstName,
                lastName: this.data.lastName,
            });
            this.closeModal(true);
        });
    }

    private closeModal(refreshGrid: boolean = false) {
        this.eventBusService.dispatch('closeUserModal', refreshGrid);
    }
}
